import { LoanTerms, NftMetadata } from "data/supabase";
import { LoanTermsV2AbiStruct } from "logic/pages/LoansPage/contracts/abis";
import { Address, Hash } from "viem";

export type Loan = Omit<LoanTermsV2AbiStruct, ""> & {
  chainId: number;
  termsStruct: LoanTermsV2AbiStruct;
  principal: bigint;
  /** Principal-weighted average of the tranche rates. Per-second, 1e18 fixed point.
   *  The interest rate alone — it excludes the insurance spread, so it is NOT the
   *  loan's APR. Prefer `apr` for display; this feeds the payment schedule. */
  blendedRate: bigint;
  /** Published APR as a fraction (0.12 = 12%): the blended rate plus the insurance
   *  spread, with the backend's per-loan carve-outs applied. Undefined when the
   *  loan is absent from the dashboard feed (non-public, closed, deduped clone) —
   *  callers fall back to `denormalizeRate(blendedRate)`. */
  apr?: number;
  erc20: ERC20V2;
  loanTermsHash: Hash;
  loanState: LoanOnchainState;
  metadata: LoanTerms["metadata"];
  nfts: LoanNfts;
  nextDeadline: number;
  currentQuote: {
    principal: bigint;
    interest: bigint;
    fees: bigint;
    total: bigint;
  };
};

type NftMetadataMap = Record<string, NftMetadata | undefined> & { totalCollateralValueUSD: number };

export type LoanNfts = {
  collection: Address;
  tokenIds: bigint[];
  metadata: NftMetadataMap;
};

export enum LoanStatus {
  Uninitialized,
  Active,
  Repaid,
  Breached,
  Liquidated,
  CollateralLiquidated,
}

export enum FeeKind {
  Origination,
  Repayment,
  Exit,
  Liquidation,
}

export type LoanOnchainState = {
  status: LoanStatus;
  repaymentCount: number;
  originationTimestamp: number;
  scaledBalance: bigint;
  deadlines: number[];
};

export type ERC20V2 = {
  address: Address;
  symbol: string;
  decimals: number;
};
